import {
	bodyCopyBold,
	Button,
	CollapsableDraggableContainer,
	CollapsableSectionHeader,
	Divider,
	IDraggableElement,
	Pullout,
	PulloutType,
	SectionContent,
} from '@aon/aon-tws-common-ui';
import React from 'react';
import styled from 'styled-components';
import { SaveButtonsGroup } from '../../../../containers/save-buttons/SaveButtonsGroup';
import ContentConstants from '../../../../contentConstants';
import { useAdminEditSurvey } from '../../../../utilities/hooks';
import { SurveyContentHelper } from '../../../../utilities/SurveyContentHelper';
import { CardLayout, HeaderButtonContainer, WarningModal } from '../../../common';
import { BrandingAndTheme } from '../../branding-and-theme';
import { SurveyDefinitions } from '../../define-survey-content/SurveyDefinitions';
import { SurveyPrivacyPolicy } from '../../define-survey-content/SurveyPrivacyPolicy';
import { QuestionsTable } from './QuestionsTable';
import { SurveyInstructions } from './SurveyInstructions';
import { SurveyIntro } from './SurveyIntro';
import { SurveyTitle } from './SurveyTitle';

export interface IAdminEditSurveyProps {
	surveyId: number;
}

const LiveSurveyWarningRow = styled.div.attrs({ className: 'row justify-content-center mb-4' })``;

const LiveSurveyWarningCol = styled.div.attrs({ className: 'col-12 col-lg-9' })``;

const LiveSurveyWarningPullout = styled(Pullout)`
	strong {
		${bodyCopyBold}
	}
`;

export const AdminEditSurvey = ({ surveyId }: IAdminEditSurveyProps) => {
	const {
		isLoadingOrSaving,
		isSurveyLive,
		isSurveyClosed,
		surveyLanguages,
		isPageDirty,
		isPageValid,
		surveyEditModel,
		surveyTitleAdmin,
		surveyIntroAdmin,
		brandingAndTheme,
		surveyQuestionsAdmin,
		activeWarningModal,
		dismissWarningModal,
		cancel,
		confirmCancel,
		sync,
		confirmSync,
		reSortSurveyContentItem,
		openedModalPopupName,
	} = useAdminEditSurvey(surveyId);

	const surveyPrivacy = (
		<div key='PrivacyPolicy'>
			<Divider type='dashed' />
			<SurveyPrivacyPolicy readOnly={isSurveyClosed} saveInModal={false}></SurveyPrivacyPolicy>
		</div>
	);
	const surveyInstruction = (
		<div key='Instructions'>
			<Divider type='dashed' />
			<SurveyInstructions supportedLanguages={surveyLanguages} isSurveyClosed={isSurveyClosed} />
		</div>
	);
	const surveyDefinition = (
		<div key='Definition'>
			<Divider type='dashed' />
			<SurveyDefinitions readOnly={isSurveyClosed} saveInModal={false} />
		</div>
	);
	const sortableItemsArr: IDraggableElement[] = SurveyContentHelper.getSortedElements(
		surveyEditModel.survey.contentItemsOrder,
		surveyPrivacy,
		surveyInstruction,
		surveyDefinition,
		isSurveyClosed,
		openedModalPopupName,
	);

	return (
		<CardLayout
			loading={isLoadingOrSaving}
			title={ContentConstants.SURVEY_ADMIN_ACTION_EDIT_SURVEY}
			header={
				<HeaderButtonContainer>
					<Button id='cancel-btn-1' color='secondary' onClick={cancel} size='md' disabled={isLoadingOrSaving}>
						{ContentConstants.BUTTONS_CANCEL}
					</Button>
					<Button id='save-btn-1' color='primary' onClick={sync} size='md' disabled={isLoadingOrSaving || !isPageDirty || !isPageValid}>
						{ContentConstants.BUTTON_SYNC}
					</Button>
				</HeaderButtonContainer>
			}
		>
			{isSurveyLive && (
				<LiveSurveyWarningRow>
					<LiveSurveyWarningCol>
						<LiveSurveyWarningPullout type={PulloutType.Warning}>
							<strong>{ContentConstants.SURVEY_ADMIN_EDIT_SURVEY_LIVE_SURVEY_WARNING_PULLOUT_HEADING}</strong>
							{ContentConstants.SURVEY_ADMIN_EDIT_SURVEY_LIVE_SURVEY_WARNING_PULLOUT_BODY}
						</LiveSurveyWarningPullout>
					</LiveSurveyWarningCol>
				</LiveSurveyWarningRow>
			)}
			<CollapsableSectionHeader headerContent={ContentConstants.SECTIONHEADERS_SURVEY_GENERAL} defaultCollapsed>
				<SectionContent>
					<SurveyTitle
						translations={surveyTitleAdmin.translations}
						validationErrors={surveyTitleAdmin.validationErrors}
						supportedLanguages={surveyLanguages}
						isSurveyClosed={isSurveyClosed}
					/>
					<Divider type='dashed' />
					<SurveyIntro translations={surveyIntroAdmin.translations} supportedLanguages={surveyLanguages} isSurveyClosed={isSurveyClosed} />
					<div style={{ position: 'relative', zIndex: '1001' }}>
						<CollapsableDraggableContainer
							dragOverStyle={{ backgroundColor: '#EEE' }}
							sortedElements={sortableItemsArr}
							handleDragEnd={reSortSurveyContentItem}
						></CollapsableDraggableContainer>
					</div>
				</SectionContent>
			</CollapsableSectionHeader>
			<CollapsableSectionHeader headerContent={ContentConstants.SECTIONHEADERS_SURVEY_BRANDING_AND_THEME} defaultCollapsed>
				<SectionContent>
					<BrandingAndTheme disabled={isSurveyClosed} {...brandingAndTheme} />
				</SectionContent>
			</CollapsableSectionHeader>
			<CollapsableSectionHeader headerContent={ContentConstants.SECTIONHEADERS_SURVEY_QUESTIONS} defaultCollapsed>
				<SectionContent>
					<QuestionsTable
						questions={surveyQuestionsAdmin.questions}
						translations={surveyQuestionsAdmin.translations}
						isSurveyClosed={isSurveyClosed}
						validations={surveyQuestionsAdmin.validations}
					/>
				</SectionContent>
			</CollapsableSectionHeader>
			<SaveButtonsGroup>
				<Button id='cancel-btn-2' color='secondary' onClick={cancel} size='lg' disabled={isLoadingOrSaving}>
					{ContentConstants.BUTTONS_CANCEL}
				</Button>
				<Button id='save-btn-2' color='primary' onClick={sync} size='lg' disabled={isLoadingOrSaving || !isPageDirty || !isPageValid}>
					{ContentConstants.BUTTON_SYNC}
				</Button>
			</SaveButtonsGroup>
			<WarningModal
				isOpen={activeWarningModal === 'discard-edits-warning-modal'}
				contentHeadline={ContentConstants.WARNING_MODAL_ACTION_CANNOT_BE_UNDONE_HEADLINE}
				contentBody={ContentConstants.WARNING_MODAL_DISCARD_UNSYNCED_EDITS_BODY}
				onClose={dismissWarningModal}
				onSubmit={confirmCancel}
			/>
			{isSurveyLive && (
				<WarningModal
					isOpen={activeWarningModal === 'sync-live-survey-edits-warning-modal'}
					contentHeadline={ContentConstants.WARNING_MODAL_ACTION_CANNOT_BE_UNDONE_HEADLINE}
					contentBody={ContentConstants.WARNING_MODAL_SYNC_LIVE_SURVEY_EDITS_BODY}
					onClose={dismissWarningModal}
					onSubmit={confirmSync}
				/>
			)}
		</CardLayout>
	);
};
